import {
  DefaultInputTreeNode,
  IdType,
  InternalDataNode,
  TraverseArgs,
  TreeState,
} from '../types';

/**
 * Finds the index in orderedIds of the closest following node that is visible (show === true)
 * @returns The index of that node
 */
export const getNextVisibleNodeIndex = ({
  currentNodeId,
  orderedIds,
  normalizedTree,
}: {
  currentNodeId: IdType;
  orderedIds: IdType[];
  normalizedTree: Record<IdType, InternalDataNode>;
}): number => {
  const initialIndex = orderedIds.findIndex((x) => x === currentNodeId);

  for (let i = initialIndex + 1; i < orderedIds.length; i++) {
    const nextNodeId = orderedIds[i];

    if (normalizedTree[nextNodeId].show) {
      return i;
    }
  }

  return -1;
};

/**
 * Finds the index in orderedIds of the closest previous node that is visible (show === true)
 * @returns The index of that node
 */
export const getPreviousVisibleNodeIndex = ({
  currentNodeId,
  orderedIds,
  normalizedTree,
}: {
  currentNodeId: IdType;
  orderedIds: IdType[];
  normalizedTree: Record<IdType, InternalDataNode>;
}): number => {
  const initialIndex = orderedIds.findIndex((x) => x === currentNodeId);

  for (let i = initialIndex - 1; i >= 0; i--) {
    const previousNodeId = orderedIds[i];

    if (normalizedTree[previousNodeId].show) {
      return i;
    }
  }

  return -1;
};

export const getLastVisibleNode = <InputTreeNode extends DefaultInputTreeNode>({
  orderedIds,
  normalizedTree,
}: {
  orderedIds: IdType[];
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
}): InternalDataNode<InputTreeNode> | undefined => {
  for (let i = orderedIds.length - 1; i > 0; i--) {
    const node = normalizedTree[orderedIds[i]];

    if (node.show) {
      return node;
    }
  }

  return undefined;
};

export const getLastDescendant = <InputTreeNode extends DefaultInputTreeNode>({
  node,
  normalizedTree,
}: {
  node: InternalDataNode<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
}): InternalDataNode<InputTreeNode> => {
  let currentNode = node;
  while (currentNode.childIds?.length) {
    currentNode =
      normalizedTree[currentNode.childIds[currentNode.childIds.length - 1]];
  }
  return currentNode;
};

export const relocateSubArray = <T>({
  arr,
  startIndex,
  endIndex,
  targetIndex,
}: {
  arr: T[];
  startIndex: number;
  endIndex: number;
  targetIndex: number;
}): [T[], T[]] => {
  const preSourceIds = arr.slice(0, startIndex);
  const sourceIds = arr.slice(startIndex, endIndex + 1);
  const postSourceIds = arr.slice(endIndex + 1);
  const sourceSubtractedFromArr = [...preSourceIds, ...postSourceIds];
  const isMoveDown = startIndex < targetIndex;
  const targetIndexOffset = isMoveDown ? -1 : 0;
  const preTargetIds = sourceSubtractedFromArr.slice(
    0,
    targetIndex + targetIndexOffset,
  );
  const postTargetIds = sourceSubtractedFromArr.slice(
    targetIndex + targetIndexOffset,
  );
  return [[...preTargetIds, ...sourceIds, ...postTargetIds], sourceIds];
};

export const checkIsDescendant = <InputTreeNode extends DefaultInputTreeNode>({
  node,
  potentialDescendantNode,
  normalizedTree,
}: {
  node: InternalDataNode<InputTreeNode>;
  potentialDescendantNode: InternalDataNode<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode<InputTreeNode>>;
}): boolean => {
  let nodes = [node];
  while (nodes.length) {
    const currentNode = nodes.pop();
    if (currentNode?.id === potentialDescendantNode.id) {
      return true;
    }
    const childNodes = currentNode?.childIds?.map((id) => normalizedTree[id]);
    if (childNodes?.length) {
      nodes = nodes.concat(childNodes);
    }
  }
  return false;
};

export const expandCollapseNode = <InputTreeNode extends DefaultInputTreeNode>(
  treeState: TreeState<InputTreeNode>,
  id: IdType,
  normalizedTree: Record<string | number, InternalDataNode<InputTreeNode>>,
  expand: boolean,
  traverse: (traverseArgs: TraverseArgs<InputTreeNode>) => void,
) => {
  const expandedIds = {
    ...treeState.expandedIds,
    [id]: expand,
  };
  const node = normalizedTree[id];
  const updatedNodes: Record<IdType, InternalDataNode<InputTreeNode>> = {};

  traverse({ currentNode: node, expandedIds, updatedNodes, normalizedTree });

  return {
    ...treeState,
    normalizedTree: {
      ...normalizedTree,
      ...updatedNodes,
    },
    expandedIds,
  };
};

export const hasFilteredChildren = <
  InputTreeNode extends DefaultInputTreeNode,
>({
  node,
  normalizedTree,
}: {
  node: InternalDataNode<InputTreeNode>;
  normalizedTree: Record<IdType, InternalDataNode>;
}) => node.childIds.some((id) => normalizedTree[id].filterShow);

export const getExpandShowState = <InputTreeNode extends DefaultInputTreeNode>({
  node,
  overrideFilter,
}: {
  node: InternalDataNode<InputTreeNode>;
  overrideFilter: boolean;
}): Pick<InternalDataNode<InputTreeNode>, 'show' | 'filterShow'> => {
  if (overrideFilter) {
    return { show: true };
  }

  if (node.filterShow === false) {
    return {
      show: false,
      filterShow: false,
    };
  }

  if (node.filterShow === true) {
    return { show: true, filterShow: true };
  }

  return { show: true, filterShow: undefined };
};

export const getCollapseShowState = <
  InputTreeNode extends DefaultInputTreeNode,
>({
  node,
}: {
  node: InternalDataNode<InputTreeNode>;
}) => {
  if (node.filterShow === false) {
    return { show: false, filterShow: false };
  }

  if (node.filterShow === true) {
    return { show: false, filterShow: true };
  }

  return { show: false };
};

export const getCollapseAllState = <
  InputTreeNode extends DefaultInputTreeNode,
>({
  node,
}: {
  node: InternalDataNode<InputTreeNode>;
}) => {
  const isRootNode = node.path.length === 0;

  if (node.filterShow === false) {
    return { show: false, filterShow: false };
  }

  if (node.filterShow === true) {
    return { show: isRootNode, filterShow: true };
  }

  return { show: isRootNode };
};
